import { useState, useEffect, useCallback } from "react";
import { useBalance, useReadContract } from "wagmi";
import { formatUnits } from "viem";
import { erc20Abi } from "viem";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { PublicKey } from "@solana/web3.js";
import type { ChainType } from "~/api/types";

/**
 * Convert a decimal string (possibly scientific notation) to smallest units
 * as bigint. Pure string math — no float round-trip.
 */
function decimalToBigInt(value: string, decimals: number): bigint {
  let s = value.trim();
  // Handle scientific notation: "1.5e-7" → expand to fixed decimal string
  if (s.includes("e") || s.includes("E")) {
    // Use Number only to determine sign/magnitude, then reconstruct
    const n = Number(s);
    if (!isFinite(n)) return 0n;
    // toFixed with enough precision to avoid rounding for our decimal range
    s = n.toFixed(Math.max(decimals, 20));
  }
  const negative = s.startsWith("-");
  if (negative) s = s.slice(1);
  const [intPart = "0", fracPart = ""] = s.split(".");
  const padded = (fracPart + "0".repeat(decimals)).slice(0, decimals);
  const raw = intPart + padded;
  const result = BigInt(raw);
  return negative ? -result : result;
}

const EVM_NATIVE = new Set([
  "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
  "0x0000000000000000000000000000000000000000",
]);

const SOLANA_NATIVE = "So11111111111111111111111111111111111111112";
const TRON_NATIVE = "T9yD14Nj9j7xAB4dbGeiX9h8unkKHxuWwb";

function isNative(address: string | undefined, chainType: ChainType = "evm"): boolean {
  if (!address) return false;
  switch (chainType) {
    case "solana":
      return address === SOLANA_NATIVE;
    case "tron":
      return address === TRON_NATIVE;
    case "evm":
    default:
      return EVM_NATIVE.has(address.toLowerCase());
  }
}

interface BalanceResult {
  balance: bigint | null;
  formatted: string | null;
  isLoading: boolean;
  refetch: () => Promise<unknown>;
}

function useSolanaBalance(
  account: string | undefined,
  tokenAddress: string | undefined,
  decimals: number,
  enabled: boolean,
): BalanceResult {
  const { primaryWallet } = useDynamicContext();
  const [state, setState] = useState<{ balance: bigint | null; isLoading: boolean }>({
    balance: null,
    isLoading: false,
  });

  const fetch = useCallback(async () => {
    if (!enabled || !account || !tokenAddress || !primaryWallet?.connector) return;

    setState((s) => ({ ...s, isLoading: true }));
    try {
      const native = tokenAddress === SOLANA_NATIVE;
      if (native) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic Labs connector lacks typed Solana methods
        const raw = (await (primaryWallet.connector as any).getBalance(account)) as
          | string
          | undefined;
        const lamports = raw ? decimalToBigInt(raw, 9) : 0n;
        setState({ balance: lamports, isLoading: false });
      } else {
        // SPL token: use Connection to get token account balance
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic Labs connector lacks typed Solana methods
        const connection = (primaryWallet.connector as any).getWalletClient?.();
        if (!connection) {
          setState({ balance: null, isLoading: false });
          return;
        }
        const owner = new PublicKey(account);
        const mint = new PublicKey(tokenAddress);
        const resp = await connection.getParsedTokenAccountsByOwner(owner, { mint });
        let total = 0n;
        for (const entry of resp.value) {
          const amount = entry.account.data.parsed?.info?.tokenAmount?.amount;
          if (amount) total += BigInt(amount);
        }
        setState({ balance: total, isLoading: false });
      }
    } catch {
      setState({ balance: null, isLoading: false });
    }
  }, [enabled, account, tokenAddress, primaryWallet]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    balance: state.balance,
    formatted: state.balance != null ? formatUnits(state.balance, decimals) : null,
    isLoading: state.isLoading,
    refetch: fetch,
  };
}

function useTronBalance(
  account: string | undefined,
  tokenAddress: string | undefined,
  decimals: number,
  enabled: boolean,
): BalanceResult {
  const { primaryWallet } = useDynamicContext();
  const [state, setState] = useState<{ balance: bigint | null; isLoading: boolean }>({
    balance: null,
    isLoading: false,
  });

  const fetch = useCallback(async () => {
    if (!enabled || !account || !tokenAddress || !primaryWallet?.connector) return;

    setState((s) => ({ ...s, isLoading: true }));
    try {
      const native = tokenAddress === TRON_NATIVE;
      if (native) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic Labs connector lacks typed Tron methods
        const raw = (await (primaryWallet.connector as any).getBalance(account)) as
          | string
          | undefined;
        const sun = raw ? decimalToBigInt(raw, 6) : 0n;
        setState({ balance: sun, isLoading: false });
      } else {
        // TRC20: use TronWeb contract call
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic Labs connector lacks typed Tron methods
        const tronWeb = (primaryWallet.connector as any).getWalletTronWeb?.();
        if (!tronWeb?.contract) {
          setState({ balance: null, isLoading: false });
          return;
        }
        const contract = await tronWeb.contract().at(tokenAddress);
        const raw = await contract.balanceOf(account).call();
        const val = BigInt(raw.toString());
        setState({ balance: val, isLoading: false });
      }
    } catch {
      setState({ balance: null, isLoading: false });
    }
  }, [enabled, account, tokenAddress, primaryWallet]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    balance: state.balance,
    formatted: state.balance != null ? formatUnits(state.balance, decimals) : null,
    isLoading: state.isLoading,
    refetch: fetch,
  };
}

/**
 * Token balance hook. Uses wagmi for EVM, Dynamic Labs for Solana/Tron.
 */
export function useTokenBalance(
  account: string | undefined,
  chainId: number | undefined,
  tokenAddress: string | undefined,
  decimals = 18,
  chainType: ChainType = "evm",
) {
  const isEvm = chainType === "evm";
  const isSolana = chainType === "solana";
  const isTron = chainType === "tron";
  const evmAccount = isEvm && account ? (account as `0x${string}`) : undefined;
  const native = isNative(tokenAddress, chainType);

  const nativeBalance = useBalance({
    address: evmAccount,
    chainId,
    query: { enabled: isEvm && !!evmAccount && !!chainId && native },
  });

  const erc20Balance = useReadContract({
    address: isEvm && tokenAddress ? (tokenAddress as `0x${string}`) : undefined,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: evmAccount ? [evmAccount] : undefined,
    chainId,
    query: { enabled: isEvm && !!evmAccount && !!chainId && !!tokenAddress && !native },
  });

  const solBalance = useSolanaBalance(
    account,
    tokenAddress,
    decimals,
    isSolana && !!account && !!tokenAddress,
  );

  const tronBalance = useTronBalance(
    account,
    tokenAddress,
    decimals,
    isTron && !!account && !!tokenAddress,
  );

  if (isSolana) return solBalance;
  if (isTron) return tronBalance;

  if (native) {
    const val = nativeBalance.data?.value;
    return {
      balance: val ?? null,
      formatted: val != null ? formatUnits(val, nativeBalance.data?.decimals ?? 18) : null,
      isLoading: nativeBalance.isLoading,
      refetch: nativeBalance.refetch,
    };
  }

  const val = erc20Balance.data as bigint | undefined;
  return {
    balance: val ?? null,
    formatted: val != null ? formatUnits(val, decimals) : null,
    isLoading: erc20Balance.isLoading,
    refetch: erc20Balance.refetch,
  };
}
